import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { FieldError } from '#/components/Form/Field/FieldError';
import { Icon } from '#/components/Icon/Icon';
import { InfoBox } from '#/components/InfoBox/InfoBox';
import { LoadingDots } from '#/components/LoadingDots/LoadingDots';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors, transition } from '#/theme/theme';
import { isDefined } from '#/utils/functional/type';
import { getRgbaString } from '#/utils/style';

import { DropContainer } from './DropContainer';
import { ImagePickerLoadingHolder } from './ImagePicker.styled';
import { ImagePickerButton } from './ImagePickerButton';
import { ImagePickerDeleteButton } from './ImagePickerDeleteButton';
import { ImagePickerOverlayButton } from './ImagePickerOverlayButton';
import { ImagePickerPreview } from './ImagePickerPreview';
import { ImagePickerInternal } from './Internal/ImagePickerInternal';

type Props = {
  image: ApiFilestackImageData | null | undefined;
  filestackPickerId?: string;
  name: string;
  onChange: (newImage: ApiFilestackImageData | null) => void;
  format?: 'logo' | 'banner' | 'avatar';
  message?: string;
  centerButton?: boolean;
  overlayButton?: boolean;
  aspectRatio?: number;
  hideRemove?: boolean;
  id?: string;
  qaId: string;
  showConfirmationModal?: () => void;
  disableCrop?: boolean;
  recommendedDimensions?: { width: number; height: number };
  label?: string;
  extraAnalyticsData?: Record<string, unknown>;
  allowSVG?: boolean;
  // Preview props
  altText?: string;
  previewCrop?: boolean;
  previewCropToCircle?: boolean;
  previewWidth?: number;
  previewHeight?: number;
  defaultImage?: ReactNode;
  saving?: boolean;
  showCropScreen?: boolean;
};

export function ImagePicker({
  id,
  image,
  filestackPickerId,
  name,
  onChange,
  previewWidth,
  previewHeight,
  format,
  message: messageProp,
  centerButton,
  overlayButton,
  aspectRatio,
  hideRemove,
  qaId,
  showConfirmationModal,
  disableCrop,
  recommendedDimensions,
  label,
  extraAnalyticsData,
  allowSVG,
  // Preview props
  altText,
  previewCrop,
  previewCropToCircle,
  defaultImage,
  saving,
  showCropScreen = false,
}: Props) {
  const { i18n } = useI18n();
  const message = messageProp ?? i18n('Choose a file or drag it here.');

  const isBannerWithOverlay = format === 'banner' && overlayButton;

  const showRecommendedDimensionsSuggestion = useMemo(() => {
    if (!recommendedDimensions || !image) return false;

    return (
      Boolean(image.width && recommendedDimensions.width > image.width) ||
      Boolean(image.height && recommendedDimensions.height > image.height)
    );
  }, [image, recommendedDimensions]);

  return (
    <ImagePickerInternal
      qaId={`${qaId}-select-image`}
      data-qa-has-image={Boolean(image)}
      id={filestackPickerId}
      name={name}
      aspectRatio={aspectRatio}
      disableCrop={disableCrop}
      onChange={onChange}
      getDimensions={Boolean(recommendedDimensions)}
      label={label}
      extraAnalyticsData={extraAnalyticsData}
      allowSVG={allowSVG}
      // TODO(@paprikka): remove this, make injectable
      showCropScreen={showCropScreen}
    >
      {/* TODO(@paprikka): stop using @style here */}
      {({ dragActive, onClick, error, loading }) => {
        if (saving || loading) {
          return (
            <ImagePickerLoadingHolder $previewWidth={previewWidth}>
              <div>
                <LoadingDots size="small" />
              </div>
            </ImagePickerLoadingHolder>
          );
        }

        return (
          <>
            {recommendedDimensions && showRecommendedDimensionsSuggestion && (
              <div style={{ marginBottom: 20 }}>
                <InfoBox $type="warning">
                  <BodySmall>
                    {i18n(
                      'Your image looks a bit small. Choose an image %(width)s x %(height)s or larger for best results.',
                      {
                        width: recommendedDimensions.width,
                        height: recommendedDimensions.height,
                      },
                    )}
                  </BodySmall>
                </InfoBox>
              </div>
            )}

            <div
              style={{
                position: 'relative',
                padding:
                  isBannerWithOverlay || (format === 'avatar' && !message)
                    ? 0
                    : '10px',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  borderRadius: 6,
                  background: `rgba(${getRgbaString(colors.skyBlue, 0.2)})`,
                  opacity: dragActive ? 1 : 0,
                  transition: `opacity ${transition}`,
                }}
              />

              <DropContainer
                position="relative"
                display="inline-flex"
                flexWrap="wrap"
                alignItems="center"
                flexDirection={centerButton ? 'column' : undefined}
                width={format === 'banner' || centerButton ? '100%' : undefined}
                $dragActive={dragActive}
                $hasImage={isDefined(image)}
                $overlayButton={overlayButton}
              >
                <div
                  style={{
                    width: format === 'banner' ? '100%' : undefined,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      width: format === 'banner' ? '100%' : undefined,
                      flexDirection: centerButton ? 'column' : undefined,
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        width: format === 'banner' ? '100%' : previewWidth,
                        height: format === 'avatar' ? previewHeight : undefined,
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          width: format === 'banner' ? '100%' : previewWidth,
                          height:
                            format === 'avatar' ? previewHeight : undefined,
                        }}
                      >
                        <ImagePickerPreview
                          image={image}
                          format={format}
                          altText={altText}
                          previewCrop={previewCrop}
                          previewCropToCircle={previewCropToCircle}
                          defaultImage={defaultImage}
                          previewWidth={previewWidth}
                          previewHeight={previewHeight}
                          allowSVG={allowSVG}
                          overlayButton={overlayButton}
                          dragActive={dragActive}
                          qaId={qaId}
                        />

                        {!hideRemove && image && (
                          <ImagePickerDeleteButton
                            type="button"
                            title={i18n('Remove')}
                            data-qa-id={`${qaId}-remove-image`}
                            onClick={() => {
                              if (showConfirmationModal) {
                                showConfirmationModal();
                              } else {
                                onChange(null);
                              }
                            }}
                            $isBannerWithOverlay={Boolean(isBannerWithOverlay)}
                            $alignToCircle={Boolean(previewCropToCircle)}
                          >
                            <Icon name="trash" size={14} />
                          </ImagePickerDeleteButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {message && overlayButton ? (
                  <ImagePickerOverlayButton
                    id={id}
                    image={image}
                    onClick={onClick}
                    message={message}
                  />
                ) : (
                  message && <ImagePickerButton id={id} message={message} />
                )}
              </DropContainer>

              {error && <FieldError errorMessage={error} />}
            </div>
          </>
        );
      }}
    </ImagePickerInternal>
  );
}
